import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X } from 'lucide-react';
import { overlayClose } from '../lib/overlayClose';

export const Calculator = ({ open, onClose }) => {
  const [display, setDisplay] = useState('0');
  const [previous, setPrevious] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (/^[0-9]$/.test(e.key)) inputDigit(e.key);
      else if (e.key === '.' || e.key === ',') inputDot();
      else if (e.key === '+' || e.key === '-' || e.key === '*' || e.key === '/') performOperation(e.key);
      else if (e.key === 'Enter' || e.key === '=') performOperation('=');
      else if (e.key === 'Backspace') backspace();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line
  }, [open, display, previous, operation, waitingForOperand]);

  if (!open) return null;

  const inputDigit = (digit) => {
    if (waitingForOperand) {
      setDisplay(String(digit));
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? String(digit) : display + digit);
    }
  };

  const inputDot = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPrevious(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const backspace = () => {
    if (display.length === 1) setDisplay('0');
    else setDisplay(display.slice(0, -1));
  };

  const performOperation = (nextOp) => {
    const inputValue = parseFloat(display);
    if (previous === null) {
      setPrevious(inputValue);
    } else if (operation) {
      const result = calculate(previous, inputValue, operation);
      setDisplay(String(result));
      setPrevious(result);
    }
    setWaitingForOperand(true);
    setOperation(nextOp === '=' ? null : nextOp);
  };

  const calculate = (a, b, op) => {
    switch (op) {
      case '+': return a + b;
      case '-': return a - b;
      case '*': return a * b;
      case '/': return b !== 0 ? a / b : 0;
      default: return b;
    }
  };

  const handleButton = (btn) => {
    if (btn === 'C') clear();
    else if (btn === '⌫') backspace();
    else if (['+','-','*','/','='].includes(btn)) performOperation(btn);
    else if (btn === '.') inputDot();
    else inputDigit(btn);
  };

  const buttons = ['C', '⌫', '/', '*', '7', '8', '9', '-', '4', '5', '6', '+', '1', '2', '3', '=', '0', '.'];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" {...overlayClose(onClose)}>
      <div className="bg-white rounded-lg p-6 w-80" onClick={(e) => e.stopPropagation()} data-testid="calculator-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-slate-900">Calculadora</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X size={20} /></button>
        </div>
        <div className="bg-slate-100 rounded-md p-4 mb-4 text-right text-3xl font-bold text-slate-900 overflow-hidden" data-testid="calc-display">
          {display}
        </div>
        <div className="grid grid-cols-4 gap-2">
          {buttons.map((btn) => (
            <Button
              key={btn}
              onClick={() => handleButton(btn)}
              className={`h-14 text-xl font-bold ${
                btn === '=' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                ['+','-','*','/'].includes(btn) ? 'bg-yellow-400 hover:bg-yellow-500 text-slate-900' :
                btn === 'C' ? 'bg-red-600 hover:bg-red-700 text-white' :
                btn === '⌫' ? 'bg-slate-200 hover:bg-slate-300 text-slate-900' :
                btn === '0' ? 'bg-slate-100 hover:bg-slate-200 text-slate-900 col-span-2' :
                'bg-slate-100 hover:bg-slate-200 text-slate-900'
              }`}
            >
              {btn}
            </Button>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3 text-center">Pressione ESC para fechar</p>
      </div>
    </div>
  );
};
